import React, { useMemo, useState } from 'react';
import { ClipboardList, Eye, Printer, Search, X } from 'lucide-react';
import Modal from '../common/Modal';
import { formatDateTime, reportEmployees, filterHistoryReports, filterReportedWorkOrders } from './productionUtils';
import { describeLineFailure } from './reportErrors';

// Line status for a saved report: lineResults are index-aligned with items. Reports
// logged before lineResults were saved have none, so their lines show no status.
const lineStatus = (report, index) => report.priority_response?.lineResults?.[index] || null;

// Detail dialog for one saved report: every line with good and reject quantities, the
// reject reason, the AL number, who reported it and, for refused lines, why.
const HistoryDetailModal = ({ report, onPrintLabels, onClose }) => {
  const items = report.items || [];
  const employees = reportEmployees(items);
  const canPrint = report.success && items.length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Report ${report.report_no || 'details'}`}
      titleIcon={<ClipboardList size={20} />}
      size="lg"
      className="production-modal"
      footer={(
        <>
          {canPrint && (
            <button
              type="button"
              className="production-btn secondary"
              onClick={() => onPrintLabels(items, null, report.created_at)}
            >
              <Printer size={16} /> Print labels
            </button>
          )}
          <button type="button" className="production-btn primary" onClick={onClose}>Close</button>
        </>
      )}
    >
      <dl className="history-detail-facts">
        <div>
          <dt>Status</dt>
          <dd>
            <span className={`status-badge ${report.success ? 'success' : 'error'}`}>
              {report.success ? 'Success' : 'Failed'}
            </span>
          </dd>
        </div>
        <div>
          <dt>Date</dt>
          <dd>{formatDateTime(report.created_at)}</dd>
        </div>
        <div>
          <dt>Reported by</dt>
          <dd>{employees.length > 0 ? employees.join(', ') : '-'}</dd>
        </div>
        {report.error_message && (
          <div>
            <dt>Error</dt>
            <dd className="error-text">{report.error_message}</dd>
          </div>
        )}
      </dl>

      <h3 className="result-section-title">Lines ({items.length})</h3>
      <ul className="history-lines">
        {items.map((item, index) => {
          const status = lineStatus(report, index);
          const failure = status && !status.success ? describeLineFailure(status) : null;
          const reject = Number(item.rejectQuantity) || 0;
          return (
            <li key={`${item.serialName}-${index}`} className={`history-line ${failure ? 'failed' : ''}`}>
              <div className="history-line-head">
                <span className="item-serial">{item.serialName}</span>
                <span className="item-part">
                  <span className="item-part-desc">{item.partDescription || item.activityName}</span>
                  {item.partName && <span className="item-part-name">{item.partName}</span>}
                </span>
                <span className="history-line-qty">
                  {item.quantity} good
                  {reject > 0 && (
                    <span className="confirm-reject">
                      , {reject} reject{item.defectCode ? ` (${item.defectCode})` : ''}
                    </span>
                  )}
                </span>
              </div>
              <div className="history-line-meta">
                {item.al && <span>AL {item.al}</span>}
                {employees.length > 1 && item.employeeName && <span>{item.employeeName}</span>}
              </div>
              {failure && (
                <>
                  <p className="result-line-reason">{failure.message}</p>
                  {failure.detail && (
                    <details className="technical-details">
                      <summary>Technical details</summary>
                      <pre>{failure.detail}</pre>
                    </details>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </Modal>
  );
};

// History view: submitted production reports, or reported work orders, with a search
// box and a day filter. Tables turn into cards on narrow screens (CSS, via data-label).
const ProductionHistory = ({
  historyViewType,
  setHistoryViewType,
  isLoadingHistory,
  historyData,
  reportedWorkOrders,
  selectedHistoryItem,
  setSelectedHistoryItem,
  onPrintLabels
}) => {
  const [search, setSearch] = useState('');
  const [day, setDay] = useState('');

  const reports = useMemo(() => filterHistoryReports(historyData, search, day), [historyData, search, day]);
  const workOrders = useMemo(() => filterReportedWorkOrders(reportedWorkOrders, search, day), [reportedWorkOrders, search, day]);
  const filtering = Boolean(search.trim() || day);

  const noMatches = (
    <div className="empty-state">
      <p>Nothing matches your search{day ? ' on that day' : ''}.</p>
      <button type="button" className="clear-search-link" onClick={() => { setSearch(''); setDay(''); }}>
        Clear search
      </button>
    </div>
  );

  return (
    <div className="production-history-view">
      {/* History view type toggle */}
      <div className="history-toolbar">
        <div className="history-view-toggle">
          <button
            className={`history-toggle-btn ${historyViewType === 'byReport' ? 'active' : ''}`}
            onClick={() => setHistoryViewType('byReport')}
            aria-pressed={historyViewType === 'byReport'}
          >
            <ClipboardList size={14} /> By Report
          </button>
          <button
            className={`history-toggle-btn ${historyViewType === 'byWorkOrder' ? 'active' : ''}`}
            onClick={() => setHistoryViewType('byWorkOrder')}
            aria-pressed={historyViewType === 'byWorkOrder'}
          >
            <ClipboardList size={14} /> By Work Order
          </button>
        </div>
        <div className="history-filters">
          <div className="work-order-search">
            <Search size={14} aria-hidden="true" />
            <input
              type="search"
              className="work-order-search-input"
              placeholder="Search report, WO, part, name..."
              aria-label="Search history"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="search-clear-btn" onClick={() => setSearch('')} aria-label="Clear search">
                <X size={14} />
              </button>
            )}
          </div>
          <input
            type="date"
            className="history-date-input"
            aria-label="Reported on"
            value={day}
            onChange={(e) => setDay(e.target.value)}
          />
        </div>
      </div>

      {isLoadingHistory ? (
        <div className="skeleton-table">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton-table-row">
              <div className="skeleton-table-cell skeleton" style={{ flex: 2 }} />
              <div className="skeleton-table-cell skeleton" style={{ flex: 1 }} />
              <div className="skeleton-table-cell skeleton" style={{ flex: 2 }} />
              <div className="skeleton-table-cell skeleton" style={{ width: '80px' }} />
            </div>
          ))}
        </div>
      ) : historyViewType === 'byReport' ? (
        // By Report View
        historyData.length === 0 ? (
          <div className="empty-state">
            <p>No production reports submitted yet.</p>
          </div>
        ) : reports.length === 0 && filtering ? noMatches : (
          <div className="history-table-container">
            <table className="history-table history-cards">
              <thead>
                <tr>
                  <th>Report No</th>
                  <th>Items</th>
                  <th>Total Qty</th>
                  <th>Reported By</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => {
                  const employees = reportEmployees(report.items);
                  return (
                    <tr key={report.id}>
                      <td data-label="Report No" className="report-no-cell">{report.report_no || '-'}</td>
                      <td data-label="Items">{report.items?.length || 0}</td>
                      <td data-label="Total Qty">{report.total_quantity}</td>
                      <td data-label="Reported By">{employees.length > 0 ? employees.join(', ') : '-'}</td>
                      <td data-label="Status">
                        <span className={`status-badge ${report.success ? 'success' : 'error'}`}>
                          {report.success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td data-label="Date">{formatDateTime(report.created_at)}</td>
                      <td className="history-actions">
                        <button
                          className="view-details-btn"
                          onClick={() => setSelectedHistoryItem(report)}
                        >
                          <Eye size={14} /> View
                        </button>
                        {report.success && report.items?.length > 0 && (
                          <button
                            className="view-details-btn print-history-btn"
                            onClick={() => onPrintLabels(report.items, null, report.created_at)}
                          >
                            <Printer size={14} /> Print
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        // By Work Order View
        reportedWorkOrders.length === 0 ? (
          <div className="empty-state">
            <p>No work orders reported yet.</p>
          </div>
        ) : workOrders.length === 0 && filtering ? noMatches : (
          <div className="history-table-container">
            <table className="history-table history-cards work-orders-history">
              <thead>
                <tr>
                  <th>Work Order</th>
                  <th>Part Description</th>
                  <th>Qty</th>
                  <th>Report No</th>
                  <th>Reported</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.map((wo, index) => (
                  <tr key={`${wo.serialName}-${wo.reportNo ?? 'none'}-${wo.reportedAt ?? index}`}>
                    <td data-label="Work Order" className="history-wo-serial">{wo.serialName}</td>
                    <td data-label="Part" title={wo.partName}>
                      <div className="part-info">
                        <span className="part-description">{wo.partDescription || wo.partName}</span>
                        {wo.partName && wo.partDescription && (
                          <span className="part-name">{wo.partName}</span>
                        )}
                      </div>
                    </td>
                    <td data-label="Qty">{wo.quantity}</td>
                    <td data-label="Report No" className="report-no-cell">{wo.reportNo || '-'}</td>
                    <td data-label="Reported">{formatDateTime(wo.reportedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {selectedHistoryItem && (
        <HistoryDetailModal
          report={selectedHistoryItem}
          onPrintLabels={onPrintLabels}
          onClose={() => setSelectedHistoryItem(null)}
        />
      )}
    </div>
  );
};

export default ProductionHistory;
